/**
 * Règles de conformité réglementaire (CDC §6.4 — ESP, F-Gaz).
 *
 * Une règle porte des versions PUBLIÉES, immuables une fois publiées — comme
 * les versions de documents (ADR-0009). Une version porte une formule
 * structurée qui calcule automatiquement les échéances ; deux familles
 * existent : une liste de jalons indépendants (ESP : IP, RP…) ou une
 * périodicité par palier de quantité (F-Gaz : charge de fluide × PRG).
 *
 * Le rattachement d'une règle à une machine n'est jamais automatique — c'est
 * l'affaire de la fiche machine, pas de cet écran.
 */
import { Fragment, useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  api,
  type ComplianceFormula,
  type ComplianceRule,
  type ComplianceRuleVersion,
} from '../api/client';
import { Champ, ErreurFormulaire, Ligne, Panneau } from '../composants/Formulaire';
import { TYPE_FORMULE } from '../domaine/conformite';

export function CompliancePage() {
  const [regles, setRegles] = useState<ComplianceRule[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [ruleType, setRuleType] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [formulaireVersion, setFormulaireVersion] = useState<ComplianceRule | null>(null);

  const charger = useCallback(async () => {
    try {
      setRegles(await api.complianceRules(false));
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Règles illisibles.');
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const creer = async (e: FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !jurisdiction.trim() || !ruleType.trim()) return;

    setEnCours(true);
    setErreur(null);
    setSucces(null);
    try {
      await api.createComplianceRule({
        label: label.trim(),
        jurisdiction: jurisdiction.trim(),
        ruleType: ruleType.trim(),
      });
      setLabel('');
      setJurisdiction('');
      setRuleType('');
      await charger();
      setSucces('Règle créée.');
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const archiver = async (regle: ComplianceRule) => {
    if (
      !window.confirm(
        `Archiver « ${regle.label} » ?\n\n` +
          'Les échéances déjà calculées restent visibles ; la règle ne sera simplement plus ' +
          'proposée au rattachement.',
      )
    ) {
      return;
    }
    setErreur(null);
    setSucces(null);
    try {
      await api.archiveComplianceRule(regle.id);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  const publier = async (regle: ComplianceRule, version: ComplianceRuleVersion) => {
    if (
      !window.confirm(
        `Publier la version ${version.versionNumber} de « ${regle.label} » ?\n\n` +
          'Une fois publiée, cette version est IMMUABLE : elle ne pourra plus être corrigée — ' +
          'seule une nouvelle version le pourra.',
      )
    ) {
      return;
    }
    setErreur(null);
    setSucces(null);
    try {
      await api.publishComplianceRuleVersion(regle.id, version.id);
      await charger();
      setSucces(`Version ${version.versionNumber} publiée.`);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Publication impossible.');
    }
  };

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Règles de conformité</h1>
          <p>
            ESP, F-Gaz… chaque règle porte des versions publiées qui calculent automatiquement les
            échéances. Le rattachement à une machine se fait depuis sa fiche.
          </p>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {succes && <div className="message succes">{succes}</div>}

      <div className="carte">
        <h2>Nouvelle règle</h2>
        <div className="carte-corps">
          <form className="saisie-ligne" onSubmit={(e) => void creer(e)}>
            <input
              type="text"
              placeholder="Libellé : « ESP — inspections périodiques »"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              aria-label="Libellé de la règle"
            />
            <input
              type="text"
              placeholder="Juridiction : « FR »"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              aria-label="Juridiction"
            />
            <input
              type="text"
              placeholder="Type : « ESP », « F-GAZ »"
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              aria-label="Type de règle"
            />
            <button disabled={enCours || !label.trim() || !jurisdiction.trim() || !ruleType.trim()}>
              {enCours ? 'Création…' : 'Créer'}
            </button>
          </form>
        </div>
      </div>

      <div className="carte">
        <h2>Règles ({regles?.length ?? 0})</h2>
        {regles === null ? (
          <div className="vide">Chargement…</div>
        ) : regles.length === 0 ? (
          <div className="vide">
            Aucune règle. Créez « ESP — inspections périodiques » ou « F-Gaz — contrôle
            d’étanchéité » pour commencer : chacune se complète ensuite d’une ou plusieurs
            versions.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Libellé</th>
                <th style={{ width: 150 }}>Code</th>
                <th style={{ width: 120 }}>Juridiction</th>
                <th style={{ width: 120 }}>Type</th>
                <th style={{ width: 100 }}>Versions</th>
                <th style={{ width: 210 }} />
              </tr>
            </thead>
            <tbody>
              {regles.map((r) => (
                <Fragment key={r.id}>
                  <tr>
                    <td>
                      <strong>{r.label}</strong>
                    </td>
                    <td>
                      <code>{r.code}</code>
                    </td>
                    <td>{r.jurisdiction}</td>
                    <td>{r.ruleType}</td>
                    <td>{r.versions.length}</td>
                    <td className="actions">
                      <button
                        className="secondaire discret"
                        onClick={() => setOuvert(ouvert === r.id ? null : r.id)}
                      >
                        {ouvert === r.id ? 'Masquer' : 'Détails'}
                      </button>
                      <button
                        className="secondaire discret danger-discret"
                        onClick={() => void archiver(r)}
                      >
                        Archiver
                      </button>
                    </td>
                  </tr>
                  {ouvert === r.id && (
                    <tr>
                      <td colSpan={6} className="detail-ligne">
                        <div className="entete-carte">
                          <strong>Versions</strong>
                          <button
                            className="secondaire discret"
                            onClick={() => setFormulaireVersion(r)}
                          >
                            Nouvelle version
                          </button>
                        </div>
                        {r.versions.length === 0 ? (
                          <div className="vide">
                            Aucune version. « Nouvelle version » en crée une, en brouillon.
                          </div>
                        ) : (
                          <table>
                            <thead>
                              <tr>
                                <th style={{ width: 50 }}>N°</th>
                                <th style={{ width: 130 }}>Formule</th>
                                <th style={{ width: 190 }}>Validité</th>
                                <th>Source</th>
                                <th>Motif</th>
                                <th style={{ width: 190 }}>Statut</th>
                                <th style={{ width: 100 }} />
                              </tr>
                            </thead>
                            <tbody>
                              {r.versions.map((v) => (
                                <tr key={v.id}>
                                  <td>{v.versionNumber}</td>
                                  <td>{TYPE_FORMULE[v.formula.kind]}</td>
                                  <td>
                                    {jour(v.validFrom)} → {v.validTo ? jour(v.validTo) : '…'}
                                  </td>
                                  <td>{v.source}</td>
                                  <td>{v.motif}</td>
                                  <td>
                                    {v.publishedAt ? (
                                      <span className="etat actif">
                                        Publiée le {jour(v.publishedAt)}
                                      </span>
                                    ) : (
                                      <span className="etat attente">Brouillon</span>
                                    )}
                                  </td>
                                  <td>
                                    {!v.publishedAt && (
                                      <button
                                        className="secondaire discret"
                                        onClick={() => void publier(r, v)}
                                      >
                                        Publier
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formulaireVersion && (
        <FormulaireVersion
          regle={formulaireVersion}
          onFermer={() => setFormulaireVersion(null)}
          onEnregistre={() => {
            void charger();
            setSucces('Version créée en brouillon.');
          }}
        />
      )}
    </>
  );
}

type TypeFormule = ComplianceFormula['kind'];

interface JalonSaisie {
  code: string;
  label: string;
  initialDelayMonths: string;
  exception: boolean;
  beforeDate: string;
  delayMonthsIfBefore: string;
  recurrenceMonths: string;
  alerts: string;
}

function jalonVide(): JalonSaisie {
  return {
    code: '',
    label: '',
    initialDelayMonths: '',
    exception: false,
    beforeDate: '',
    delayMonthsIfBefore: '',
    recurrenceMonths: '',
    alerts: '',
  };
}

interface PrgSaisie {
  fluide: string;
  prg: string;
}

interface PalierSaisie {
  minQuantity: string;
  recurrenceMonths: string;
}

/**
 * Nouvelle version d'une règle.
 *
 * Le choix de la formule change entièrement le sous-formulaire : les deux
 * familles ne partagent aucun champ, les mélanger n'aiderait personne.
 */
function FormulaireVersion({
  regle,
  onFermer,
  onEnregistre,
}: {
  regle: ComplianceRule;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [validFrom, setValidFrom] = useState(aujourdHui());
  const [validTo, setValidTo] = useState('');
  const [source, setSource] = useState('');
  const [motif, setMotif] = useState('');
  const [typeFormule, setTypeFormule] = useState<TypeFormule>('FIXED_SCHEDULE');

  // --- FIXED_SCHEDULE ---------------------------------------------------
  const [jalons, setJalons] = useState<JalonSaisie[]>([jalonVide()]);

  // --- THRESHOLD_TABLE ----------------------------------------------------
  const [milestoneCode, setMilestoneCode] = useState('');
  const [seuilLabel, setSeuilLabel] = useState('');
  const [quantityAttributeCode, setQuantityAttributeCode] = useState('');
  const [fluidAttributeCode, setFluidAttributeCode] = useState('');
  const [doublingAttributeCode, setDoublingAttributeCode] = useState('');
  const [gwpTable, setGwpTable] = useState<PrgSaisie[]>([{ fluide: '', prg: '' }]);
  const [paliers, setPaliers] = useState<PalierSaisie[]>([
    { minQuantity: '', recurrenceMonths: '' },
  ]);
  const [alertesSeuil, setAlertesSeuil] = useState('');

  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const majJalon = (index: number, changement: Partial<JalonSaisie>) =>
    setJalons((liste) => liste.map((j, i) => (i === index ? { ...j, ...changement } : j)));
  const ajouterJalon = () => setJalons((liste) => [...liste, jalonVide()]);
  const retirerJalon = (index: number) =>
    setJalons((liste) => liste.filter((_, i) => i !== index));

  const majGwp = (index: number, changement: Partial<PrgSaisie>) =>
    setGwpTable((liste) => liste.map((g, i) => (i === index ? { ...g, ...changement } : g)));
  const ajouterGwp = () => setGwpTable((liste) => [...liste, { fluide: '', prg: '' }]);
  const retirerGwp = (index: number) =>
    setGwpTable((liste) => liste.filter((_, i) => i !== index));

  const majPalier = (index: number, changement: Partial<PalierSaisie>) =>
    setPaliers((liste) => liste.map((p, i) => (i === index ? { ...p, ...changement } : p)));
  const ajouterPalier = () =>
    setPaliers((liste) => [...liste, { minQuantity: '', recurrenceMonths: '' }]);
  const retirerPalier = (index: number) =>
    setPaliers((liste) => liste.filter((_, i) => i !== index));

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    if (!validFrom || !source.trim() || !motif.trim()) {
      setErreur(new Error('La date de validité, la source et le motif sont obligatoires.'));
      return;
    }

    let formula: ComplianceFormula;

    if (typeFormule === 'FIXED_SCHEDULE') {
      const milestones = jalons
        .filter((j) => j.code.trim() !== '' && j.label.trim() !== '')
        .map((j) => ({
          code: j.code.trim(),
          label: j.label.trim(),
          initialDelayMonths: Number(j.initialDelayMonths) || 0,
          ...(j.exception && j.beforeDate
            ? {
                initialDelayMonthsIfCommissionedBefore: {
                  beforeDate: j.beforeDate,
                  delayMonths: Number(j.delayMonthsIfBefore) || 0,
                },
              }
            : {}),
          recurrenceMonths: Number(j.recurrenceMonths) || 0,
          alerts: { beforeDueMonths: parseAlertes(j.alerts) },
        }));

      if (milestones.length === 0) {
        setErreur(new Error('Au moins un jalon est requis, avec son code et son libellé.'));
        return;
      }
      formula = { kind: 'FIXED_SCHEDULE', milestones };
    } else {
      if (
        !milestoneCode.trim() ||
        !seuilLabel.trim() ||
        !quantityAttributeCode.trim() ||
        !fluidAttributeCode.trim()
      ) {
        setErreur(
          new Error(
            'Le code du jalon, son libellé et les deux champs techniques sont obligatoires.',
          ),
        );
        return;
      }

      const gwpEntries: [string, number][] = gwpTable
        .filter((g) => g.fluide.trim() !== '' && g.prg.trim() !== '')
        .map((g) => [g.fluide.trim(), Number(g.prg)]);
      const thresholds = paliers
        .filter((p) => p.minQuantity.trim() !== '' && p.recurrenceMonths.trim() !== '')
        .map((p) => ({
          minQuantity: Number(p.minQuantity),
          recurrenceMonths: Number(p.recurrenceMonths),
        }));

      if (gwpEntries.length === 0) {
        setErreur(new Error('Au moins un fluide et son PRG sont requis.'));
        return;
      }
      if (thresholds.length === 0) {
        setErreur(new Error('Au moins un palier est requis.'));
        return;
      }

      formula = {
        kind: 'THRESHOLD_TABLE',
        milestoneCode: milestoneCode.trim(),
        label: seuilLabel.trim(),
        quantityAttributeCode: quantityAttributeCode.trim(),
        fluidAttributeCode: fluidAttributeCode.trim(),
        gwpTable: Object.fromEntries(gwpEntries),
        thresholds,
        ...(doublingAttributeCode.trim()
          ? { doublingAttributeCode: doublingAttributeCode.trim() }
          : {}),
        alerts: { beforeDueMonths: parseAlertes(alertesSeuil) },
      };
    }

    setEnCours(true);
    setErreur(null);
    try {
      await api.createComplianceRuleVersion(regle.id, {
        validFrom,
        ...(validTo ? { validTo } : {}),
        source: source.trim(),
        motif: motif.trim(),
        formula,
      });
      onEnregistre();
      onFermer();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={`Nouvelle version — ${regle.label}`}
      sousTitre="Créée en brouillon : elle ne calculera des échéances qu’une fois publiée."
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Créer la version'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void enregistrer(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <Ligne>
          <Champ libelle="Valide à partir du" requis>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </Champ>
          <Champ libelle="Valide jusqu’au" aide="Laissez vide : pas de fin connue.">
            <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
          </Champ>
        </Ligne>

        <Champ libelle="Source" requis aide="Le texte réglementaire qui fonde cette version.">
          <input
            type="text"
            placeholder="Arrêté du 20 novembre 2017, article 5"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
        </Champ>

        <Champ libelle="Motif" requis aide="Pourquoi cette version est créée.">
          <input
            type="text"
            placeholder="Alignement sur le texte en vigueur"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
        </Champ>

        <Champ libelle="Formule">
          <div className="cases">
            <label className="case">
              <input
                type="radio"
                name="type-formule"
                checked={typeFormule === 'FIXED_SCHEDULE'}
                onChange={() => setTypeFormule('FIXED_SCHEDULE')}
              />
              Échéance fixe
            </label>
            <label className="case">
              <input
                type="radio"
                name="type-formule"
                checked={typeFormule === 'THRESHOLD_TABLE'}
                onChange={() => setTypeFormule('THRESHOLD_TABLE')}
              />
              Palier de seuil
            </label>
          </div>
        </Champ>

        {typeFormule === 'FIXED_SCHEDULE' ? (
          <div className="formule-jalons">
            {jalons.map((j, i) => (
              <div className="jalon" key={i}>
                <div className="entete-carte">
                  <strong>Jalon {i + 1}</strong>
                  <button
                    type="button"
                    className="secondaire discret danger-discret"
                    disabled={jalons.length === 1}
                    onClick={() => retirerJalon(i)}
                  >
                    Retirer
                  </button>
                </div>
                <Ligne>
                  <Champ libelle="Code" requis aide="« IP », « RP »…">
                    <input
                      type="text"
                      value={j.code}
                      onChange={(e) => majJalon(i, { code: e.target.value })}
                    />
                  </Champ>
                  <Champ libelle="Libellé" requis>
                    <input
                      type="text"
                      value={j.label}
                      onChange={(e) => majJalon(i, { label: e.target.value })}
                    />
                  </Champ>
                </Ligne>
                <Ligne>
                  <Champ libelle="Délai initial (mois)" requis aide="Depuis la mise en service.">
                    <input
                      type="number"
                      min="0"
                      value={j.initialDelayMonths}
                      onChange={(e) => majJalon(i, { initialDelayMonths: e.target.value })}
                    />
                  </Champ>
                  <Champ libelle="Périodicité (mois)" requis aide="Ensuite.">
                    <input
                      type="number"
                      min="0"
                      value={j.recurrenceMonths}
                      onChange={(e) => majJalon(i, { recurrenceMonths: e.target.value })}
                    />
                  </Champ>
                </Ligne>
                <Champ libelle="Alertes (mois avant échéance)" aide="Séparées par des virgules : 6, 2">
                  <input
                    type="text"
                    value={j.alerts}
                    onChange={(e) => majJalon(i, { alerts: e.target.value })}
                  />
                </Champ>
                <label className="case">
                  <input
                    type="checkbox"
                    checked={j.exception}
                    onChange={(e) => majJalon(i, { exception: e.target.checked })}
                  />
                  Exception si mise en service avant une date
                </label>
                {j.exception && (
                  <Ligne>
                    <Champ libelle="Mise en service avant le">
                      <input
                        type="date"
                        value={j.beforeDate}
                        onChange={(e) => majJalon(i, { beforeDate: e.target.value })}
                      />
                    </Champ>
                    <Champ libelle="Délai alternatif (mois)">
                      <input
                        type="number"
                        min="0"
                        value={j.delayMonthsIfBefore}
                        onChange={(e) => majJalon(i, { delayMonthsIfBefore: e.target.value })}
                      />
                    </Champ>
                  </Ligne>
                )}
              </div>
            ))}
            <button type="button" className="secondaire discret" onClick={ajouterJalon}>
              Ajouter un jalon
            </button>
          </div>
        ) : (
          <div className="formule-seuils">
            <Ligne>
              <Champ libelle="Code du jalon" requis aide="« CONTROLE_ETANCHEITE »">
                <input
                  type="text"
                  value={milestoneCode}
                  onChange={(e) => setMilestoneCode(e.target.value)}
                />
              </Champ>
              <Champ libelle="Libellé" requis>
                <input
                  type="text"
                  value={seuilLabel}
                  onChange={(e) => setSeuilLabel(e.target.value)}
                />
              </Champ>
            </Ligne>
            <Ligne>
              <Champ libelle="Champ technique — quantité" requis aide="« charge_fluide_kg »">
                <input
                  type="text"
                  value={quantityAttributeCode}
                  onChange={(e) => setQuantityAttributeCode(e.target.value)}
                />
              </Champ>
              <Champ libelle="Champ technique — fluide" requis aide="« fluide_frigorigene »">
                <input
                  type="text"
                  value={fluidAttributeCode}
                  onChange={(e) => setFluidAttributeCode(e.target.value)}
                />
              </Champ>
            </Ligne>

            <Champ libelle="Table des PRG" requis aide="Pouvoir de réchauffement global par fluide.">
              <div className="liste-paires">
                {gwpTable.map((g, i) => (
                  <div className="saisie-ligne" key={i}>
                    <input
                      type="text"
                      placeholder="Fluide : R404A"
                      value={g.fluide}
                      onChange={(e) => majGwp(i, { fluide: e.target.value })}
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="PRG : 3922"
                      value={g.prg}
                      onChange={(e) => majGwp(i, { prg: e.target.value })}
                    />
                    <button
                      type="button"
                      className="secondaire discret danger-discret"
                      disabled={gwpTable.length === 1}
                      onClick={() => retirerGwp(i)}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
                <button type="button" className="secondaire discret" onClick={ajouterGwp}>
                  Ajouter un fluide
                </button>
              </div>
            </Champ>

            <Champ libelle="Paliers" requis aide="Quantité minimale déclenchant chaque périodicité.">
              <div className="liste-paires">
                {paliers.map((p, i) => (
                  <div className="saisie-ligne" key={i}>
                    <input
                      type="number"
                      min="0"
                      placeholder="Quantité mini (kg)"
                      value={p.minQuantity}
                      onChange={(e) => majPalier(i, { minQuantity: e.target.value })}
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Périodicité (mois)"
                      value={p.recurrenceMonths}
                      onChange={(e) => majPalier(i, { recurrenceMonths: e.target.value })}
                    />
                    <button
                      type="button"
                      className="secondaire discret danger-discret"
                      disabled={paliers.length === 1}
                      onClick={() => retirerPalier(i)}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
                <button type="button" className="secondaire discret" onClick={ajouterPalier}>
                  Ajouter un palier
                </button>
              </div>
            </Champ>

            <Champ
              libelle="Champ technique — doublement"
              aide="Facultatif : un champ booléen qui double les seuils quand il vaut vrai."
            >
              <input
                type="text"
                value={doublingAttributeCode}
                onChange={(e) => setDoublingAttributeCode(e.target.value)}
              />
            </Champ>

            <Champ libelle="Alertes (mois avant échéance)" aide="Séparées par des virgules : 6, 2">
              <input
                type="text"
                value={alertesSeuil}
                onChange={(e) => setAlertesSeuil(e.target.value)}
              />
            </Champ>
          </div>
        )}
      </form>
    </Panneau>
  );
}

/** « 6, 2 » → [6, 2] — les valeurs vides ou non numériques sont ignorées. */
function parseAlertes(saisie: string): number[] {
  return saisie
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
    .map(Number)
    .filter((n) => Number.isFinite(n));
}

function jour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function aujourdHui(): string {
  // Découpe de l'ISO local, et non `toISOString()` qui bascule en UTC et
  // donnerait « demain » en soirée.
  const d = new Date();
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jourDuMois = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jourDuMois}`;
}
